import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import {
  VIRTUAL_MACHINE_LABELS,
  VIRTUAL_VALIDITY_LABELS,
  VIRTUAL_CATEGORY_LABELS,
} from '../data/virtual-products.js';
import { youtubeIdOf, youtubeEmbedUrl } from '../utils/video.js';
import { LINE_ID, LINE_ADD_URL } from '../constants/contact.js';
import { analyticsEvent, gaItem } from '../utils/analytics.js';
import { formatVirtualCountdown, isVirtualExpired, parseVirtualExpiry } from '../utils/virtualAvailability.js';
import './VirtualDetailPage.css';

// 取得商品變體（種類/角色）；沒設 variants 的商品 fallback 成單一「標準」
const variantsOf = (p) =>
  p?.variants?.length ? p.variants : (p ? [{ id: 'default', name: '標準', price: p.price ?? 0 }] : []);

function useVirtualCountdown(product) {
  const [now, setNow] = useState(Date.now());
  const expiry = parseVirtualExpiry(product?.unpublishAt);

  useEffect(() => {
    if (expiry == null) return undefined;
    const current = Date.now();
    setNow(current);
    if (expiry <= current) return undefined;
    const delay = expiry - Date.now() <= 24 * 60 * 60 * 1000 ? 1000 : 60000;
    const timer = window.setInterval(() => {
      const next = Date.now();
      setNow(next);
      if (next >= expiry) window.clearInterval(timer);
    }, delay);
    return () => window.clearInterval(timer);
  }, [expiry]);

  const expired = isVirtualExpired(product, now);
  const label = expired ? 'SOLD OUT · 登記已截止' : formatVirtualCountdown(product?.unpublishAt, now);
  return { expired, label };
}

/**
 * 虛擬商品詳情呈現元件（純呈現，product 必為非 null）。
 * 一般會員詳情頁與免登入分享頁共用同一份版型。
 */
function VirtualDetail({ product }) {
  const navigate = useNavigate();
  const [variantId, setVariantId] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const { expired, label: countdownLabel } = useVirtualCountdown(product);

  // 圖庫 slides：影片（可多支）依序排在前面，再接多張圖片；都沒有時 fallback 主題配色卡
  // YouTube 連結 <video> 播不了，標記 youtubeId 改走 iframe 內嵌
  const slides = useMemo(() => {
    const list = [];
    // 後端已對舊資料做 previewUrl → previewUrls 的 fallback，這裡再保險一層（mock 資料只有 previewUrl）
    const videos = (product?.previewUrls?.length ? product.previewUrls : [product?.previewUrl]).filter(Boolean);
    videos.forEach(src => {
      list.push({ type: 'video', src, youtubeId: youtubeIdOf(src) });
    });
    (product?.images || []).forEach(src => { if (src) list.push({ type: 'image', src }); });
    if (list.length === 0) list.push({ type: 'card' });
    return list;
  }, [product]);

  // 切換商品後主圖重置到第一格
  useEffect(() => { setActiveSlide(0); }, [product]);

  const machineLabel = VIRTUAL_MACHINE_LABELS[product.machine];
  const categoryLabel = VIRTUAL_CATEGORY_LABELS[product.category];
  const validityLabel = VIRTUAL_VALIDITY_LABELS[product.validity];

  // 種類/角色：像實體商品一樣在詳情頁選，價格隨選變動，並帶進登記頁
  const variants = useMemo(() => variantsOf(product), [product]);
  const hasVariants = (product.variants?.length || 0) > 1;
  const selectedId = variantId || variants[0]?.id;
  const selected = variants.find(v => v.id === selectedId) || variants[0];

  useEffect(() => {
    if (!product || !selected) return;
    analyticsEvent('view_item', {
      currency: 'TWD',
      value: Number(selected.price ?? 0),
      items: [gaItem({
        id: selected.id, name: product.name, price: selected.price,
        category: 'virtual_product', variantName: selected.name,
      }, 0)],
    });
  }, [product, selected]);

  const goRegister = () => {
    if (expired) return;
    navigate(`/register?item=${product.slug ?? product.id}&variant=${selectedId}`);
  };
  const trackRegisterClick = () => {
    analyticsEvent('select_item', {
      item_list_name: 'virtual_products',
      currency: 'TWD',
      value: Number(selected?.price ?? 0),
      items: [gaItem({ id: selectedId, name: product.name, price: selected?.price, category: 'virtual_product', variantName: selected?.name }, 0)],
    });
  };


  const playVideo = (e) => {
    const vid = e.currentTarget.querySelector('video');
    if (vid) { vid.currentTime = 0; vid.play().catch(() => {}); }
  };
  const stopVideo = (e) => {
    const vid = e.currentTarget.querySelector('video');
    if (vid) { vid.pause(); vid.currentTime = 0; }
  };

  // 環狀切換：超出頭尾自動繞回（與實體商品詳情頁一致）
  const goToSlide = (i) => {
    const len = slides.length;
    if (len === 0) return;
    setActiveSlide(((i % len) + len) % len);
  };
  const current = slides[activeSlide] || slides[0];

  return (
    <main className="vd">
      <button type="button" className="vd-back" onClick={() => navigate(-1)}>← 上一頁</button>
      <nav className="vd-crumbs">
        <Link to="/virtual">虛擬商品</Link> / <span>{machineLabel}</span> / <b>{product.name}</b>
      </nav>

      <div className="vd-grid">
        {/* 預覽圖庫：主圖（影片 / 圖片）+ 縮圖列，可往右瀏覽 */}
        <div className="vd-media">
          <div
            className={`vd-stage${expired ? ' ended' : ''}`}
            onMouseEnter={current.type === 'video' ? playVideo : undefined}
            onMouseLeave={current.type === 'video' ? stopVideo : undefined}
          >
            <div className="vd-thumb">
              <div className="vd-scan" />
            </div>
            {current.type === 'image' && (
              <img className="vd-img" src={current.src} alt={product.name} />
            )}
            {current.type === 'video' && (current.youtubeId
              ? (
                <iframe
                  className="vd-yt"
                  src={youtubeEmbedUrl(current.youtubeId)}
                  title={`${product.name} 預覽影片`}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              )
              : (
                <>
                  <video muted loop playsInline preload="none" src={current.src} />
                  <span className="vd-live">LIVE PREVIEW · 滑鼠移過播放</span>
                </>
              )
            )}
            {slides.length > 1 && (
              <>
                <button type="button" className="vd-nav prev" aria-label="上一張"
                  onClick={() => goToSlide(activeSlide - 1)}>‹</button>
                <button type="button" className="vd-nav next" aria-label="下一張"
                  onClick={() => goToSlide(activeSlide + 1)}>›</button>
              </>
            )}
            <div className="vd-frame" />
            {expired && <span className="vd-soldout">SOLD OUT<small>登記已截止</small></span>}
          </div>
          {slides.length > 1 && (
            <div className="vd-thumbs">
              {slides.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className={`vd-thumbitem${i === activeSlide ? ' on' : ''}`}
                  onClick={() => goToSlide(i)}
                >
                  {s.type === 'image'
                    ? <img src={s.src} alt="" />
                    : <span className="vd-thumbcard">{s.type === 'video' ? '▶' : '🎯'}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 資訊 */}
        <div className="vd-info">
          <div className="vd-title-row">
            <h1>{product.name}</h1>
            {expired && <span className="vd-ended-chip">已結束</span>}
          </div>
          {countdownLabel && (
            <div className={`vd-countdown${expired ? ' ended' : ''}`} role="status">
              <span className="vd-countdown-dot" />{countdownLabel}
            </div>
          )}
          <div className="vd-badges">
            <span className={`machine-${product.machine}`}>{machineLabel}</span>
            <span className="cat">{categoryLabel}</span>
            {product.tags?.includes('new') && <span className="new">NEW</span>}
            {product.tags?.includes('limited') && <span className="limit">LIMITED</span>}
          </div>

          <div className="vd-price">
            <span className="p"><small>NT$</small>{selected?.price ?? product.price}</span>
            <span className="tax">數位商品 · 無需運費 · {validityLabel}</span>
          </div>

          {/* 商品說明：後台沒填就不顯示 */}
          {product.description?.trim() && (
            <p className="vd-desc">{product.description}</p>
          )}

          {hasVariants && (
            <div className="vd-variants">
              <span className="vd-variants-label">種類 / 角色</span>
              <div className="vd-variant-chips">
                {variants.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    className={`vd-variant-chip${v.id === selectedId ? ' on' : ''}`}
                    onClick={() => setVariantId(v.id)}
                  >
                    <b>{v.name}</b><small>NT${v.price}</small>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="vd-cta">
            <span style={{ display: 'contents' }} onPointerDown={expired ? undefined : trackRegisterClick}>
            <button className="btn btn-primary" onClick={goRegister} disabled={expired}>
              <Icon name={expired ? 'lock' : 'edit'} size={16} />
              {expired ? '登記已截止' : `我要登記${hasVariants ? `（${selected?.name}）` : ''}`}
            </button>
            </span>
          </div>

          {!expired ? <div className="line-note">
            <span className="line-note-ic"><Icon name="mail" size={20} /></span>
            <p>
              <b>網站上只須填寫登入信箱。</b>
              <br />
              初次購買請加官方 LINEID：{LINE_ID}
              <br />
              由專人協助核對 / 提供登入資料；
              <br />
              資料僅用於本次作業，不作其他用途。
            </p>
            <button type="button" className="line-btn" onClick={() => window.open(LINE_ADD_URL, '_blank', 'noopener,noreferrer')}>加官方 LINE</button>
          </div> : (
            <div className="vd-ended-note">
              此商品的登記時間已結束，內容保留供會員查看，無法再新增登記。
            </div>
          )}

          <div className="vd-warn">
            <Icon name="shield" size={18} />
            <p><b>購買前請確認是否正確。</b>不確定可詢問LINE官方。</p>
          </div>

          <div className="vd-specs">
            <h4>商品資訊 · SPECS</h4>
            <div className="vd-spec"><span className="k">相容機台</span><span className="v">{machineLabel}</span></div>
            <div className="vd-spec"><span className="k">內容類型</span><span className="v">{categoryLabel}</span></div>
            <div className="vd-spec"><span className="k">使用效期</span><span className="v">{validityLabel}</span></div>
            {/* <div className="vd-spec"><span className="k">發送方式</span><span className="v">Email 啟用碼 + QR Code</span></div> */}
          </div>

          <div className="vd-how">
            <h4>如何運作 · HOW IT WORKS</h4>
            <div className="vd-how-grid">
              <div className="vd-step"><span className="n">1</span><div><b>送出登記</b><small>填卡名 / 機台帳號，一次可多人</small></div></div>
              <div className="vd-step"><span className="n">2</span><div><b>我們確認</b><small>確認後主動通知你應付金額</small></div></div>
              <div className="vd-step"><span className="n">3</span><div><b>完成付款</b><small>轉帳 / LINE Pay / PayPal</small></div></div>
              {/* <div className="vd-step"><span className="n">4</span><div><b>發送啟用碼</b><small>Email 發碼，登入 {machineLabel} 輸入即綁定</small></div></div> */}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default VirtualDetail;
